/**
 *
 * @param importer A function that returns a promise
 * @param resolver A function that receives the output of the importer (if any)
 * @param options
 * @param options.logger A logger instance that implements ILogger (console-like)
 * @param options.maxLoadAttempts Maximum number of attempts to load the SDK
 * @param options.maxResolveAttempts Maximum number of attempts to call the resolver
 *
 * @example
 * ```
 * loadCalendlySdk = sdkLoader(
 *   () => loadScript("https://assets.calendly.com/assets/external/widget.js"),
 *   () => window.SomeGlobalVariableThatTheImportedScriptDefines,
 * );
 * ```
 *
 * @example
 * ```
 * loadCalendlySdk = sdkLoader(
 *   () => import("mapbox-gl"),
 *   (module) => module.default,
 * );
 * ```
 */
export function sdkLoader(importer, resolver, { logger, maxLoadAttempts = 3, maxResolveAttempts = 3, }) {
    return () => new Promise((resolve, reject) => {
        let loadAttemptsRemaining = maxLoadAttempts;
        let resolveAttemptsRemaining = maxResolveAttempts;
        const attemptResolve = (exportsIfAny) => {
            const resolvedValue = resolver(exportsIfAny);
            if (resolvedValue !== undefined) {
                resolve(resolvedValue);
                return;
            }
            if ((resolveAttemptsRemaining -= 1) === 0) {
                logger?.error("Failed to resolve SDK - aborting.", resolver.toString());
                reject(new Error("Failed to resolve SDK."));
                return;
            }
            setTimeout(() => attemptResolve(exportsIfAny), 1000);
        };
        const attemptLoad = () => {
            importer()
                .then(attemptResolve)
                .catch((error) => {
                if ((loadAttemptsRemaining -= 1) === 0) {
                    logger?.error("Failed to load SDK - aborting.", importer.toString());
                    reject(error);
                    return;
                }
                attemptLoad();
            });
        };
        attemptLoad();
    });
}
//# sourceMappingURL=sdk-loader.js.map